import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadPhoto } from "./uploadPhoto";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function DeliveryPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [note, setNote] = useState("");
  const [togglingInPerson, setTogglingInPerson] = useState(false);
  const fileInputRef = useRef(null);

  async function loadOrder() {
    const res = await fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() });
    const d = await res.json();
    if (res.ok) setOrder(d);
    setLoading(false);
  }

  useEffect(() => { loadOrder(); }, [orderId]);

  const isSeller = order?.sellerId === user?.id;
  const isBuyer = order?.buyerId === user?.id;

  async function handlePhotoUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(files.map(f => uploadPhoto(f)));
      setPhotos(prev => [...prev, ...urls]);
    } catch { window.alert("Photo upload failed. Please try again."); }
    finally { setUploading(false); e.target.value = ""; }
  }

  function removePhoto(index) { setPhotos(prev => prev.filter((_, i) => i !== index)); }

  async function handleToggleInPerson() {
    setTogglingInPerson(true);
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${orderId}/in-person`, { method: "PUT", headers: authHeaders() });
      if (res.ok) await loadOrder();
      else { const d = await res.json(); window.alert(d.error || "Could not update"); }
    } catch { window.alert("Something went wrong"); }
    finally { setTogglingInPerson(false); }
  }

  async function handleSellerDelivery(e) {
    e.preventDefault();
    if (!order.isInPerson && photos.length === 0) { window.alert("Please upload at least one photo as proof of shipment."); return; }
    setSubmitting(true);
    try {
      if (!order.isInPerson) {
        const proofRes = await fetch(`${API_BASE_URL}/payments/delivery`, { method: "POST", headers: authHeaders(), body: JSON.stringify({ orderId, fileUrl: photos[0], trackingNumber: trackingNumber.trim() || null, note: note.trim() || null }) });
        if (!proofRes.ok) { const d = await proofRes.json(); window.alert(d.error || "Could not submit proof"); return; }
      }
      await fetch(`${API_BASE_URL}/orders/${orderId}/seller-confirm`, { method: "PUT", headers: authHeaders() });
      window.alert(order.isInPerson ? "In-person meeting confirmed! The buyer has been notified to confirm receipt." : "Order shipped! The buyer has been notified to confirm receipt.");
      navigate("/orders");
    } catch { window.alert("Something went wrong"); }
    finally { setSubmitting(false); }
  }

  async function handleBuyerConfirm() {
    if (!window.confirm("Confirm you received this order as described? The seller will receive their payment.")) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/orders/${orderId}/buyer-confirm`, { method: "PUT", headers: authHeaders() });
      if (res.ok) { window.alert("Receipt confirmed! Seller payment is being processed."); navigate("/orders"); }
      else { const d = await res.json(); window.alert(d.error || "Could not confirm receipt"); }
    } catch { window.alert("Something went wrong"); }
    finally { setSubmitting(false); }
  }

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 680, margin: "0 auto", padding: "32px 20px" },
    title: { fontSize: 28, fontWeight: 800, marginBottom: 8, color: "#0f172a" },
    subtitle: { fontSize: 14, color: "#64748b", marginBottom: 28 },
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 20, marginBottom: 16, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    sectionTitle: { fontSize: 16, fontWeight: 700, marginBottom: 12, color: "#1e293b" },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.8)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", minHeight: 90, resize: "vertical", outline: "none" },
    uploadBtn: { width: "100%", padding: "14px", background: "rgba(59,130,246,0.06)", border: "2px dashed rgba(59,130,246,0.3)", borderRadius: 12, color: "#3b82f6", cursor: "pointer", fontWeight: 700, fontSize: 14, marginBottom: 12, textAlign: "center" },
    photoGrid: { display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 },
    photoWrap: { position: "relative" },
    photoThumb: { width: 90, height: 90, objectFit: "cover", borderRadius: 10, border: "1px solid rgba(59,130,246,0.15)" },
    removeBtn: { position: "absolute", top: -6, right: -6, background: "#ef4444", border: "none", borderRadius: "50%", width: 22, height: 22, color: "#fff", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 },
    uploadingBanner: { display: "flex", alignItems: "center", gap: 10, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#3b82f6" },
    primaryBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, width: "100%", marginBottom: 12, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
    confirmBtn: { background: "#16a34a", color: "#fff", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15, width: "100%", marginBottom: 12 },
    disputeBtn: { background: "rgba(217,119,6,0.08)", color: "#d97706", border: "1px solid rgba(217,119,6,0.25)", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, width: "100%" },
    successBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 12, padding: 16, marginBottom: 16 },
    infoBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(59,130,246,0.06)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 12, padding: 16, marginBottom: 16 },
    awaitingBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(124,58,237,0.06)", border: "1px solid rgba(124,58,237,0.2)", borderRadius: 12, padding: 16, marginBottom: 16 },
    warningBanner: { display: "flex", alignItems: "flex-start", gap: 12, background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: 12, padding: 16, marginBottom: 16 },
    inPersonBanner: { display: "flex", alignItems: "center", gap: 12, background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 12, padding: 16, marginBottom: 16 },
    metaRow: { display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 },
    metaLabel: { color: "#64748b" },
    metaValue: { color: "#1e293b", fontWeight: 600 },
    backBtn: { background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 14, marginBottom: 20, padding: 0 },
    toggleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "rgba(255,255,255,0.7)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 12, marginBottom: 16, cursor: "pointer", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
    toggleLabel: { fontSize: 14, fontWeight: 600, color: "#1e293b" },
    toggleSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
    toggle: (on) => ({ width: 44, height: 24, borderRadius: 12, background: on ? "#16a34a" : "rgba(100,116,139,0.3)", position: "relative", transition: "background 0.2s", flexShrink: 0, cursor: "pointer" }),
    toggleDot: (on) => ({ position: "absolute", top: 3, left: on ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }),
  };

  if (loading) return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  if (!order) return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Order not found</div>
    </div>
  );

  const orderTitle = order.items?.map(i => i.listingItem?.title).filter(Boolean).join(", ") || "Order";

  return (
    <div style={styles.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={styles.main}>
        <button style={styles.backBtn} onClick={() => navigate("/orders")}>← Back to Orders</button>
        <div style={styles.title}>{isSeller ? "Confirm Order Fulfillment" : "Confirm Receipt"}</div>
        <div style={styles.subtitle}>{orderTitle}</div>

        <div style={styles.card}>
          <div style={styles.sectionTitle}>Order Summary</div>
          <div style={styles.metaRow}><span style={styles.metaLabel}>Buyer</span><span style={styles.metaValue}>{order.buyer?.fullName || "—"}</span></div>
          <div style={styles.metaRow}><span style={styles.metaLabel}>Seller</span><span style={styles.metaValue}>{order.seller?.fullName || "—"}</span></div>
          <div style={styles.metaRow}><span style={styles.metaLabel}>Total</span><span style={{ color: "#1e40af", fontWeight: 700 }}>${Number(order.totalAmount).toFixed(2)}</span></div>
          <div style={styles.metaRow}>
            <span style={styles.metaLabel}>Status</span>
            <span style={{ fontWeight: 600, color: order.status === "IN_PROGRESS" ? "#7c3aed" : order.status === "COMPLETED" ? "#16a34a" : "#3b82f6" }}>
              {order.status === "IN_PROGRESS" ? "⏳ Awaiting Delivery" : order.status?.replace(/_/g, " ")}
            </span>
          </div>
          {order.isInPerson && (
            <div style={{ marginTop: 8, padding: "6px 12px", background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 8, fontSize: 12, color: "#16a34a", fontWeight: 700 }}>
              🤝 In-Person Transaction
            </div>
          )}
        </div>

        {!order.sellerConfirmed && !order.buyerConfirmed && (
          <div style={styles.toggleRow} onClick={handleToggleInPerson}>
            <div>
              <div style={styles.toggleLabel}>🤝 In-Person Transaction</div>
              <div style={styles.toggleSub}>{order.isInPerson ? "Meeting in person — no shipping proof required" : "Switch to in-person if you're meeting locally"}</div>
            </div>
            <div style={styles.toggle(order.isInPerson)}><div style={styles.toggleDot(order.isInPerson)} /></div>
          </div>
        )}

        {order.isInPerson && !order.sellerConfirmed && (
          <div style={styles.inPersonBanner}>
            <span style={{ fontSize: 24 }}>🤝</span>
            <div>
              <div style={{ fontWeight: 700, color: "#16a34a", marginBottom: 2 }}>In-Person Transaction</div>
              <div style={{ fontSize: 13, color: "#475569" }}>{isSeller ? "Once you've handed over the item, click confirm below to notify the buyer." : "Your seller will confirm once you've met and the item has been handed over."}</div>
            </div>
          </div>
        )}

        {order.deliveryProofs?.length > 0 && (
          <div style={styles.card}>
            <div style={styles.sectionTitle}>Proof Submitted</div>
            {order.deliveryProofs.map(proof => (
              <div key={proof.id} style={{ padding: "10px 0", borderBottom: "1px solid rgba(59,130,246,0.08)" }}>
                {proof.trackingNumber && <div style={{ fontSize: 13, marginBottom: 4 }}><span style={{ color: "#64748b" }}>Tracking: </span><span style={{ color: "#3b82f6", fontWeight: 600 }}>{proof.trackingNumber}</span></div>}
                {proof.note && <div style={{ fontSize: 13, color: "#64748b", marginBottom: 4 }}>{proof.note}</div>}
                {proof.fileUrl && <img src={proof.fileUrl} alt="Delivery proof" style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 10, marginTop: 6 }} />}
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{new Date(proof.createdAt).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        )}

        {isSeller && !order.sellerConfirmed && (
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{order.isInPerson ? "Confirm In-Person Handoff" : "Upload Shipping Proof"}</div>
            {!order.isInPerson && (
              <div style={styles.infoBanner}>
                <span style={{ fontSize: 20 }}>📋</span>
                <span style={{ fontSize: 13, color: "#3b82f6" }}>Upload photos of the item, packaging, or shipping label. Add a tracking number if available.</span>
              </div>
            )}
            <form onSubmit={handleSellerDelivery}>
              {!order.isInPerson && (
                <>
                  <label style={styles.label}>Photos *</label>
                  {photos.length > 0 && (
                    <div style={styles.photoGrid}>
                      {photos.map((url, i) => (
                        <div key={i} style={styles.photoWrap}>
                          <img src={url} alt={`Proof ${i + 1}`} style={styles.photoThumb} />
                          <button type="button" style={styles.removeBtn} onClick={() => removePhoto(i)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploading ? (
                    <div style={styles.uploadingBanner}><span>⏳</span> Uploading photo...</div>
                  ) : (
                    <>
                      <div style={styles.uploadBtn} onClick={() => fileInputRef.current?.click()}>📷 {photos.length > 0 ? "Add More Photos" : "Click to Upload Photos"}</div>
                      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotoUpload} />
                    </>
                  )}
                  <label style={styles.label}>Tracking Number (Optional)</label>
                  <input style={styles.input} placeholder="e.g. 1Z999AA10123456784" value={trackingNumber} onChange={e => setTrackingNumber(e.target.value)} />
                  <label style={styles.label}>Note (Optional)</label>
                  <textarea style={styles.textarea} placeholder="Any notes about the shipment..." value={note} onChange={e => setNote(e.target.value)} />
                </>
              )}
              <button type="submit" style={{ ...styles.primaryBtn, opacity: submitting || uploading ? 0.5 : 1, background: order.isInPerson ? "#16a34a" : "#3b82f6" }} disabled={submitting || uploading}>
                {submitting ? "Confirming..." : order.isInPerson ? "🤝 Confirm Item Handed Over" : "📦 Confirm Shipped — Notify Buyer"}
              </button>
            </form>
          </div>
        )}

        {isSeller && order.sellerConfirmed && !order.buyerConfirmed && (
          <div style={styles.awaitingBanner}>
            <span style={{ fontSize: 28 }}>⏳</span>
            <div>
              <div style={{ fontWeight: 700, color: "#7c3aed", marginBottom: 2 }}>Awaiting Confirmation</div>
              <div style={{ fontSize: 13, color: "#7c3aed", opacity: 0.8 }}>{order.isInPerson ? "Waiting for buyer to confirm they received the item in person" : "Waiting for buyer to confirm they received the order"}</div>
            </div>
          </div>
        )}

        {isSeller && order.buyerConfirmed && (
          <div style={styles.successBanner}>
            <span style={{ fontSize: 28 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, color: "#16a34a", marginBottom: 2 }}>Order Completed</div>
              <div style={{ fontSize: 13, color: "#16a34a", opacity: 0.8 }}>Buyer confirmed receipt. Your payment will be released shortly.</div>
            </div>
          </div>
        )}

        {isBuyer && !order.buyerConfirmed && order.sellerConfirmed && (
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{order.isInPerson ? "Confirm You Received This Item" : "Confirm You Received This Order"}</div>
            <div style={styles.warningBanner}>
              <span style={{ fontSize: 20 }}>⚠️</span>
              <span style={{ fontSize: 13, color: "#b45309" }}>Only confirm if you received the item exactly as described. Once confirmed the seller receives payment.</span>
            </div>
            <button style={{ ...styles.confirmBtn, opacity: submitting ? 0.5 : 1 }} onClick={handleBuyerConfirm} disabled={submitting}>
              {submitting ? "Confirming..." : order.isInPerson ? "🤝 Yes, I Received This Item" : "✅ Yes, I Received This Order"}
            </button>
            <button style={styles.disputeBtn} onClick={() => navigate(`/dispute/${order.id}`)}>⚠️ Something is wrong — File Dispute</button>
          </div>
        )}

        {isBuyer && order.buyerConfirmed && (
          <div style={styles.successBanner}>
            <span style={{ fontSize: 28 }}>✅</span>
            <div>
              <div style={{ fontWeight: 700, color: "#16a34a", marginBottom: 2 }}>Receipt Confirmed</div>
              <div style={{ fontSize: 13, color: "#16a34a", opacity: 0.8 }}>Seller payment is being processed</div>
            </div>
          </div>
        )}

        {isBuyer && !order.sellerConfirmed && (
          <div style={styles.infoBanner}>
            <span style={{ fontSize: 20 }}>⏳</span>
            <span style={{ fontSize: 13, color: "#3b82f6" }}>{order.isInPerson ? "Waiting for the seller to confirm the in-person handoff." : "Waiting for the seller to ship and confirm the order before you can confirm receipt."}</span>
          </div>
        )}
      </div>
    </div>
  );
}